import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IQuestionSubType } from "./IQuestionSubType";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addQuestionType } from "../../Redux/Slices/QuestionType/AddQuestionTypeSlice";
import { EditQuestionType } from "../../Redux/Slices/QuestionType/EditQuestionTypeSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { EditQuestionSubType } from "../../Redux/Slices/QuestionSubType/EditQuestionSubTypeSlice";
import { addQuestionSubType } from "../../Redux/Slices/QuestionSubType/AddQuestionSubTypeSlice";
import { QuestionSubTypelist } from "../../Redux/Slices/QuestionSubType/QuestionSubTypelistSlice";
import NumberInputField from "../../Components/NumberInput/NumberInputField";

interface QuestionSubTypeFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IQuestionSubType;
  setInitialValues: React.Dispatch<React.SetStateAction<IQuestionSubType>>;
}

const QuestionSubTypeForm: FunctionComponent<QuestionSubTypeFormProps> = (
  props
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(QuestionSubTypelist({}));
  }, []);

  const { data: listQuestions } = useAppSelector(
    (state) => state.QuestionSubTypelistSlice //List_Question_SubTypeSliceReducer
  );

  const QuestionList = Array.isArray(listQuestions?.data)
    ? listQuestions?.data
        .map((listQuestions) => ({
          label: listQuestions?.topic_name!,
          value: listQuestions.id,
        }))
    : [];

  const userSchema = z.object({
    id: z.number().optional(),
    subtype_name: z.string({ message: "Please enter question subtype name " })
      .min(1, { message: "Please enter subtype_name" })
      .max(50, { message: "subtype_name cannot exceed 50 characters" }),
    questiontype_id: z.number({
      required_error: "Please select question type",
      invalid_type_error: "Please select question type",
    }),
    sequence: z
      .number({ message: "Please enter sequence" })
      .min(1, { message: "Sequence must be at least 1" })
      .max(100, { message: "Sequence cannot exceed 100" }),

    status: z.boolean({
      message: "Please select the status",
    }),
   description: z
        .string()
        .min(1, { message: "Please enter description " })
        .max(250, { message: "description cannot exceed 250 characters" }), 
  });
  const methods = useForm<IQuestionSubType>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    },

    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IQuestionSubType> = (val) => {
    if (val.id) {
      dispatch(EditQuestionSubType(val));
    } else {
      dispatch(addQuestionSubType(val));
    }
  };
  

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const QuestionTypeSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_content"
      >
        <TextInputFieldC
          name="subtype_name"
          label="Question sub type"
          type="text"
        />
        <Select
          name="questionType"
          label="Question type"
          options={QuestionList ?? []}
          value={
            QuestionList?.find(
              (list) => list.value === methods.getValues("questiontype_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("questiontype_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />
        <NumberInputField name="sequence" label="Sequence" type="number" />
        <TextAreaInputField
          name="description"
          label="Description"
          type="text"
        />
        <Select
          name="status"
          label="Status"
          options={QuestionTypeSatus}
          value={
            QuestionTypeSatus?.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(QuestionSubTypeForm);
